import { Link } from 'react-router-dom';
import { Mail, MapPin, Phone, Linkedin, Facebook } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_praxis-launch/artifacts/9x9a538r_MeritumPraxis%20Logo.png";

export const Footer = () => {
  const currentYear = 2026;

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Candidates', path: '/candidates' },
    { name: 'Organizations', path: '/organizations' },
    { name: 'Pricing', path: '/pricing' },
    { name: 'Contact', path: '/contact' },
  ];

  const legalLinks = [
    { name: 'Disclaimer', path: '/disclaimer' },
    { name: 'Privacy Policy', path: '/privacy-policy' },
    { name: 'Terms of Use', path: '/terms-of-use' },
  ];

  return (
    <footer data-testid="footer" className="bg-meritum-navy text-white border-t-4 border-meritum-gold">
      {/* Main Footer */}
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6">
              <img
                src={LOGO_URL}
                alt="MeritumPraxis"
                className="h-20 w-auto object-contain bg-white/10 p-2 rounded-lg drop-shadow-lg"
              />
            </Link>
            <p className="font-body text-slate-300 text-sm leading-relaxed mb-6">
              Progress Made Visible. Building verified professional profiles that grow with your achievements.
            </p>
            <div className="flex items-center gap-4">
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-white/10 rounded-full hover:bg-meritum-gold hover:text-meritum-navy transition-colors duration-200"
                data-testid="social-linkedin"
              >
                <Linkedin size={18} />
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-white/10 rounded-full hover:bg-meritum-gold hover:text-meritum-navy transition-colors duration-200"
                data-testid="social-x"
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              </a>
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-white/10 rounded-full hover:bg-meritum-gold hover:text-meritum-navy transition-colors duration-200"
                data-testid="social-facebook"
              >
                <Facebook size={18} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-lg font-semibold text-meritum-gold mb-6">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    onClick={() => window.scrollTo(0, 0)}
                    className="font-body text-sm text-slate-300 hover:text-meritum-gold transition-colors duration-200"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-heading text-lg font-semibold text-meritum-gold mb-6">
              Legal
            </h4>
            <ul className="space-y-3">
              {legalLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    onClick={() => window.scrollTo(0, 0)}
                    className="font-body text-sm text-slate-300 hover:text-meritum-gold transition-colors duration-200"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-heading text-lg font-semibold text-meritum-gold mb-6">
              Contact Us
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Mail size={18} className="text-meritum-gold mt-0.5 flex-shrink-0" />
                <a
                  href="mailto:info@meritumpraxis.co.za"
                  className="font-body text-sm text-slate-300 hover:text-meritum-gold transition-colors duration-200"
                  data-testid="contact-email"
                >
                  info@meritumpraxis.co.za
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={18} className="text-meritum-gold mt-0.5 flex-shrink-0" />
                <span className="font-body text-sm text-slate-300">
                  South Africa
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="font-body text-sm text-slate-400 text-center md:text-left">
              © {currentYear} MeritumPraxis (Pty) Ltd. All rights reserved.
            </p>
            <p className="font-body text-sm text-slate-400 text-center md:text-right">
              Digital Admin & Design by{' '}
              <a
                href="https://www.successfuljournals.co.za/get-started"
                target="_blank"
                rel="noopener noreferrer"
                className="text-meritum-gold hover:underline"
                data-testid="successful-journals-link"
              >
                Successful Journals
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};
